import { useState, useEffect, useCallback } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Loader2, Plus, X, Sparkles, FlaskConical, BarChart3, Brain,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Trash2,
} from "lucide-react";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DESIGN_TYPES = [
  { value: "full_factorial", label: "Full Factorial", desc: "Tests all combinations. Best for 2-4 factors." },
  { value: "fractional_factorial", label: "Fractional Factorial", desc: "Subset of runs. Good for 5+ factors, screening." },
  { value: "taguchi", label: "Taguchi (Orthogonal Array)", desc: "Robust design. Minimizes variation, fewer runs." },
  { value: "ccd", label: "Central Composite (RSM)", desc: "Response Surface. Finds optimal settings with quadratic fit." },
];

const EMPTY_FACTOR = { name: "", type: "continuous", low: "", high: "", levels: ["Low", "High"] };

export default function DoeTab({ projectId, token }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);

  // New design form
  const [showForm, setShowForm] = useState(false);
  const [designType, setDesignType] = useState("full_factorial");
  const [responseName, setResponseName] = useState("Defect Rate");
  const [factors, setFactors] = useState([
    { name: "", type: "continuous", low: "", high: "", levels: ["Low", "High"] },
    { name: "", type: "continuous", low: "", high: "", levels: ["Low", "High"] },
  ]);
  const [generating, setGenerating] = useState(false);

  // Active design
  const [activeDesign, setActiveDesign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/doe`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.designs || [];
        setDesigns(list);
        if (list.length > 0 && !activeDesign) setActiveDesign(list[list.length - 1]);
      }
    } catch (e) {
      console.error("Failed to load DOE:", e);
    } finally {
      setLoading(false);
    }
  }, [projectId, token, activeDesign]);

  useEffect(() => { load(); }, [load]);

  const addFactor = () => setFactors(prev => [...prev, { ...EMPTY_FACTOR }]);
  const removeFactor = (idx) => { if (factors.length > 2) setFactors(prev => prev.filter((_, i) => i !== idx)); };
  const updateFactor = (idx, field, val) => setFactors(prev => prev.map((f, i) => i === idx ? { ...f, [field]: val } : f));

  const generateDesign = async () => {
    const valid = factors.filter(f => f.name.trim());
    if (valid.length < 2) { toast.error("Define at least 2 factors"); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/doe/generate-design`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ factors: valid, design_type: designType, response_name: responseName }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Generation failed"); }
      const design = await res.json();
      setDesigns(prev => [...prev, design]);
      setActiveDesign(design);
      setShowForm(false);
      toast.success(`${design.runs.length}-run ${designType.replace("_", " ")} design generated`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const updateResponse = (runIdx, value) => {
    if (!activeDesign) return;
    setActiveDesign(prev => ({
      ...prev,
      runs: prev.runs.map((r, i) => i === runIdx ? { ...r, _response: value === "" ? null : parseFloat(value) } : r),
    }));
  };

  const saveResults = async () => {
    if (!activeDesign) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/doe/${activeDesign.design_id}/results`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ runs: activeDesign.runs }),
      });
      if (!res.ok) throw new Error("Failed to save");
      const updated = await res.json();
      setActiveDesign(updated);
      setDesigns(prev => prev.map(d => d.design_id === updated.design_id ? updated : d));
      toast.success("Results saved and analyzed");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const aiInterpret = async () => {
    if (!activeDesign?.analysis) { toast.error("Run analysis first by entering response values"); return; }
    setInterpreting(true);
    try {
      const res = await fetch(`${API}/ai/doe-interpret`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          project_id: projectId,
          design_id: activeDesign.design_id,
          analysis: activeDesign.analysis,
          factors: activeDesign.factors,
          design_type: activeDesign.design_type,
        }),
      });
      if (!res.ok) throw new Error("AI interpretation failed");
      const interp = await res.json();
      setActiveDesign(prev => ({ ...prev, ai_interpretation: interp }));
      setDesigns(prev => prev.map(d => d.design_id === activeDesign.design_id ? { ...d, ai_interpretation: interp } : d));
      toast.success("AI interpretation complete");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setInterpreting(false);
    }
  };

  const deleteDesign = async (designId) => {
    try {
      await fetch(`${API}/projects/${projectId}/doe/${designId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setDesigns(prev => prev.filter(d => d.design_id !== designId));
      if (activeDesign?.design_id === designId) setActiveDesign(null);
      toast.success("Design deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading DOE module...</div>;

  const analysis = activeDesign?.analysis;
  const interp = activeDesign?.ai_interpretation;
  const factorNames = activeDesign?.factors?.map(f => f.name) || [];

  return (
    <div className="space-y-6" data-testid="doe-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlaskConical className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-heading font-semibold">Design of Experiments</h3>
            <p className="text-xs text-muted-foreground">Factorial, Taguchi, and Response Surface designs</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)} data-testid="new-doe-btn">
          <Plus className="w-4 h-4 mr-1" /> New Design
        </Button>
      </div>

      {/* Design selector */}
      {designs.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {designs.map(d => (
            <button
              key={d.design_id}
              onClick={() => setActiveDesign(d)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                activeDesign?.design_id === d.design_id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border hover:border-primary/50"
              }`}
              data-testid={`doe-design-chip-${d.design_id}`}
            >
              {d.design_type.replace("_", " ")} ({d.runs.length} runs)
              {d.analysis && <CheckCircle2 className="w-3 h-3 ml-1 inline" />}
            </button>
          ))}
        </div>
      )}

      {/* New Design Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="doe-form">
          <h4 className="font-heading font-semibold mb-4">Create New DOE Design</h4>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label>Design Type</Label>
              <select value={designType} onChange={e => setDesignType(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="doe-type-select">
                {DESIGN_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground">{DESIGN_TYPES.find(dt => dt.value === designType)?.desc}</p>
            </div>
            <div className="space-y-2">
              <Label>Response Variable</Label>
              <Input value={responseName} onChange={e => setResponseName(e.target.value)} placeholder="e.g., Defect Rate, Yield" data-testid="doe-response-name" />
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <Label>Factors</Label>
              <Button variant="ghost" size="sm" onClick={addFactor}><Plus className="w-3 h-3 mr-1" /> Add Factor</Button>
            </div>
            {factors.map((f, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`doe-factor-${idx}`}>
                <div className="col-span-3 space-y-1">
                  <Label className="text-[10px]">Name</Label>
                  <Input value={f.name} onChange={e => updateFactor(idx, "name", e.target.value)} placeholder="Factor name" data-testid={`doe-factor-name-${idx}`} />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px]">Type</Label>
                  <select value={f.type} onChange={e => updateFactor(idx, "type", e.target.value)} className="w-full h-10 px-2 rounded-md border border-input bg-background text-sm" data-testid={`doe-factor-type-${idx}`}>
                    <option value="continuous">Continuous</option>
                    <option value="categorical">Categorical</option>
                  </select>
                </div>
                {f.type === "continuous" ? (
                  <>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-[10px]">Low</Label>
                      <Input type="number" value={f.low} onChange={e => updateFactor(idx, "low", e.target.value)} placeholder="Min" data-testid={`doe-factor-low-${idx}`} />
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-[10px]">High</Label>
                      <Input type="number" value={f.high} onChange={e => updateFactor(idx, "high", e.target.value)} placeholder="Max" data-testid={`doe-factor-high-${idx}`} />
                    </div>
                  </>
                ) : (
                  <div className="col-span-6 space-y-1">
                    <Label className="text-[10px]">Levels (comma-separated)</Label>
                    <Input value={f.levels.join(", ")} onChange={e => updateFactor(idx, "levels", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Level1, Level2, Level3" />
                  </div>
                )}
                <div className="col-span-1 flex justify-end">
                  {factors.length > 2 && (
                    <Button variant="ghost" size="sm" onClick={() => removeFactor(idx)} className="text-destructive"><X className="w-3 h-3" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Button onClick={generateDesign} disabled={generating} data-testid="generate-doe-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</> : <><FlaskConical className="w-4 h-4 mr-1" /> Generate Design</>}
          </Button>
        </div>
      )}

      {/* Active Design: Experiment Matrix */}
      {activeDesign && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="doe-matrix">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-heading font-semibold">{activeDesign.design_type.replace("_", " ")} Design</h4>
              <p className="text-xs text-muted-foreground">{activeDesign.runs.length} runs, {activeDesign.factors.length} factors, Response: {activeDesign.response_name}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={aiInterpret} disabled={interpreting || !analysis} className="gap-1" data-testid="ai-interpret-doe-btn">
                {interpreting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {interpreting ? "Interpreting..." : "AI Interpret"}
                <CreditCostTag cost={10} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => deleteDesign(activeDesign.design_id)} className="text-destructive" data-testid="delete-doe-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm" data-testid="doe-runs-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground w-16">Run</th>
                  {factorNames.map(fn => (
                    <th key={fn} className="text-left py-2 px-2 font-medium text-primary">{fn}</th>
                  ))}
                  <th className="text-left py-2 px-2 font-medium text-amber-400">{activeDesign.response_name}</th>
                </tr>
              </thead>
              <tbody>
                {activeDesign.runs.map((run, idx) => (
                  <tr key={idx} className="border-b border-border hover:bg-muted/30">
                    <td className="py-2 px-2 text-muted-foreground font-mono text-xs">{run.run_id || idx + 1}</td>
                    {factorNames.map(fn => (
                      <td key={fn} className="py-2 px-2 font-mono text-xs">{typeof run[fn] === "number" ? run[fn].toFixed(2) : run[fn]}</td>
                    ))}
                    <td className="py-2 px-2">
                      <Input
                        type="number"
                        step="any"
                        value={run._response ?? ""}
                        onChange={e => updateResponse(idx, e.target.value)}
                        placeholder="Enter result"
                        className="w-28 text-sm h-8"
                        data-testid={`doe-response-${idx}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Button onClick={saveResults} disabled={saving} data-testid="save-doe-results-btn">
            {saving ? "Saving & Analyzing..." : "Save & Analyze Results"}
          </Button>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="doe-analysis">
          <button onClick={() => setShowAnalysis(!showAnalysis)} className="flex items-center justify-between w-full mb-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h4 className="font-heading font-semibold">DOE Analysis Results</h4>
            </div>
            {showAnalysis ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showAnalysis && (
            <div className="space-y-6">
              {/* Summary metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-background border border-border rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Grand Mean</p>
                  <p className="text-lg font-bold font-mono">{analysis.grand_mean}</p>
                </div>
                <div className="bg-background border border-border rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Std Dev</p>
                  <p className="text-lg font-bold font-mono">{analysis.std_dev}</p>
                </div>
                <div className="bg-background border border-border rounded-lg p-3">
                  <p className="text-[10px] text-emerald-400 uppercase tracking-wider">Best</p>
                  <p className="text-lg font-bold font-mono text-emerald-400" data-testid="doe-best-response">{analysis.best_response}</p>
                </div>
                <div className="bg-background border border-border rounded-lg p-3">
                  <p className="text-[10px] text-destructive uppercase tracking-wider">Worst</p>
                  <p className="text-lg font-bold font-mono text-destructive">{analysis.worst_response}</p>
                </div>
              </div>

              {/* Main Effects */}
              <div>
                <h5 className="font-medium text-sm mb-3">Main Effects (ranked by magnitude)</h5>
                <div className="space-y-2">
                  {analysis.main_effects?.map((me, i) => {
                    const maxEffect = analysis.main_effects[0]?.effect_size || 1;
                    const pct = Math.min(100, (me.effect_size / maxEffect) * 100);
                    return (
                      <div key={i} className="flex items-center gap-3" data-testid={`doe-effect-${me.factor}`}>
                        <span className="w-32 text-sm font-medium truncate">{me.factor}</span>
                        <div className="flex-1 h-6 bg-muted/30 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${me.significant ? "bg-primary" : "bg-muted-foreground/40"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono w-16 text-right">{me.effect_size}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${me.significant ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-muted text-muted-foreground border-border"}`}>
                          p={me.p_value < 0.001 ? "<0.001" : me.p_value.toFixed(3)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Significant factors highlight */}
              {analysis.significant_factors?.length > 0 && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg" data-testid="doe-significant-factors">
                  <h5 className="font-medium text-sm text-emerald-400 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Significant Factors (p &lt; 0.05)
                  </h5>
                  <div className="flex gap-2 flex-wrap">
                    {analysis.significant_factors.map((sf, i) => (
                      <span key={i} className="px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded text-xs font-medium text-emerald-400">{sf}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Optimal settings */}
              {analysis.optimal_settings && Object.keys(analysis.optimal_settings).length > 0 && (
                <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg" data-testid="doe-optimal-settings">
                  <h5 className="font-medium text-sm text-primary mb-2">Optimal Settings (Maximize Response)</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(analysis.optimal_settings).map(([k, v]) => (
                      <div key={k} className="bg-background/50 rounded p-2">
                        <p className="text-[10px] text-muted-foreground">{k}</p>
                        <p className="text-sm font-bold font-mono">{typeof v === "number" ? v.toFixed(2) : v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interactions */}
              {analysis.interactions?.length > 0 && (
                <div>
                  <h5 className="font-medium text-sm mb-3">Key Interactions</h5>
                  <div className="space-y-2">
                    {analysis.interactions.slice(0, 5).map((ix, i) => (
                      <div key={i} className="p-3 bg-background border border-border rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{ix.factors.join(" x ")}</span>
                          <span className="text-xs font-mono text-amber-400">effect = {ix.interaction_effect}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Interpretation */}
      {interp && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="doe-ai-interpretation">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-5 h-5 text-primary" />
            <h4 className="font-heading font-semibold">AI Interpretation</h4>
          </div>

          {interp.interpretation && (
            <p className="text-sm text-muted-foreground mb-4">{interp.interpretation}</p>
          )}

          {interp.key_findings?.length > 0 && (
            <div className="mb-4">
              <h5 className="font-medium text-sm mb-2">Key Findings</h5>
              <ul className="space-y-1">
                {interp.key_findings.map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-2"><span className="text-primary">&bull;</span>{f}</li>
                ))}
              </ul>
            </div>
          )}

          {interp.optimal_recommendation && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
              <h5 className="font-medium text-sm text-emerald-400 mb-2">Recommended Settings</h5>
              {interp.optimal_recommendation.settings && (
                <div className="flex gap-3 flex-wrap mb-2">
                  {Object.entries(interp.optimal_recommendation.settings).map(([k, v]) => (
                    <span key={k} className="px-2 py-1 bg-emerald-500/20 rounded text-xs font-mono">{k}: {v}</span>
                  ))}
                </div>
              )}
              {interp.optimal_recommendation.expected_improvement && (
                <p className="text-xs text-muted-foreground">{interp.optimal_recommendation.expected_improvement}</p>
              )}
            </div>
          )}

          {interp.interaction_warnings?.length > 0 && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
              <h5 className="font-medium text-sm text-amber-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Interaction Warnings
              </h5>
              <ul className="space-y-1">
                {interp.interaction_warnings.map((w, i) => (
                  <li key={i} className="text-xs text-muted-foreground">{w}</li>
                ))}
              </ul>
            </div>
          )}

          {interp.implementation_notes?.length > 0 && (
            <div>
              <h5 className="font-medium text-sm mb-2">Implementation Notes</h5>
              <ul className="space-y-1">
                {interp.implementation_notes.map((n, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2"><span>&rarr;</span>{n}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {designs.length === 0 && !showForm && (
        <div className="text-center py-12 bg-card border border-border rounded-lg" data-testid="doe-empty">
          <FlaskConical className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-2">No experiments designed yet</p>
          <p className="text-xs text-muted-foreground mb-4">Create a DOE to systematically test factor effects on your process</p>
          <Button onClick={() => setShowForm(true)} data-testid="doe-empty-new-btn"><Plus className="w-4 h-4 mr-1" /> Create First Design</Button>
        </div>
      )}
    </div>
  );
}
